import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobColors } from "../../../hooks/useJobColors";
import { useRoleGroupColors } from "../../../hooks/useRoleColors";
import { INK, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";
import type { DayStats } from "../../../utils/models";
import type { SwatchLegendItem } from "../../UIToolkit/molecules/SwatchLegend";
import { getSectionTone } from "../WeeklyGapView/consts";
import type { GapsJobTotal } from "./gapsDayModel";
import type { GapsPickupBonuses } from "./gapsPickupBonuses";
import { GapsCensusAndHppd } from "./metrics/GapsCensusAndHppd";
import { GapsJobShortfalls } from "./metrics/GapsJobShortfalls";
import { GapsMetricsDetail } from "./metrics/GapsMetricsDetail";
import { METRIC_LABEL_SX } from "./metrics/gapsMetricsStyles";
import type { GapsDayHppd } from "./useGapsDayModel";

type Props = {
	/** Per-job totals for the day, or `undefined` while they are not final (see `GapsHomeModel.isDayReady`). */
	jobTotals: GapsJobTotal[] | undefined;
	dayStats: DayStats | undefined;
	/** HPPD and target as the Day Schedule header computes them — never `dayStats.hppd`. */
	dayHppd: GapsDayHppd | undefined;
	/** The day's pick-up bonuses (P3-11), building-wide; absent until the day is loaded. */
	pickupBonuses: GapsPickupBonuses | undefined;
	date: DateTime;
	isExpanded: boolean;
	onToggle: () => void;
	/** True when a saved scope is narrowing the numbers, so the line says they are not the building's. */
	isNarrowed: boolean;
};

/**
 * The one line that answers "how short am I, and what is today costing".
 *
 * Collapsed it is the whole of the day's problem stated in staff — a dot and `−2` or `✓` per job — with
 * census and HPPD on the right. Every job is printed whether or not it is short, the artifact's `✓`
 * included: a job that is covered is an answer, not an absence, and the eye needs the same row every
 * day to compare against. Each dot is the job's own colour, keyed by name in the drawer.
 *
 * Nothing here is computed. The gap totals come from the same `dayTotals` the group headers read; census,
 * PPD and cost come from `DayItem.dayStats` and HPPD from `useHppdByDimension` — each the source the Day
 * Schedule header prints — so the phone and the desktop cannot report different numbers for one day. A day with no stats (a URL
 * date outside the loaded week, for the moment the shell takes to redirect it) shows the gaps alone
 * rather than a row of zeros that would read as a real, empty day.
 */
export const GapsMetricsLine: FC<Props> = ({ jobTotals, dayStats, dayHppd, pickupBonuses, date, isExpanded, onToggle, isNarrowed }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const jobColor = useJobColors();
	const roleGroupColor = useRoleGroupColors();
	const reduceMotion = useReducedMotion();

	// One colour per section, the same one its badge on the groups below is filled with (`getSectionTone`), so
	// the key names a dot by the colour the user also sees on the group.
	// DEFAULT: totals that are not final yet draw no dots and no key — an empty line for that moment, never a
	// provisional `−N` the settled grouping may not agree with.
	const shownTotals = useMemo<GapsJobTotal[]>(() => jobTotals ?? [], [jobTotals]);
	const dots = useMemo<ReadonlyMap<string, string>>(
		() =>
			new Map(
				shownTotals.map((total) => [
					total.jobLabel,
					getSectionTone({ shortRole: total.jobLabel, isJobSection: total.isJobSection, jobId: total.jobId }, chosenDepartment.role_groups, jobColor, roleGroupColor).badge,
				]),
			),
		[shownTotals, chosenDepartment.role_groups, jobColor, roleGroupColor],
	);
	const legend = useMemo<SwatchLegendItem[]>(
		() => shownTotals.map(({ jobLabel }) => ({ id: jobLabel, label: jobLabel, fill: dots.get(jobLabel), round: true })),
		[shownTotals, dots],
	);

	return (
		<Box sx={{ bgcolor: SURFACE }}>
			<ButtonBase
				data-testid="gaps-metrics-line"
				onClick={onToggle}
				aria-expanded={isExpanded}
				sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, px: 1.625, py: 1, borderTop: `1px solid ${NEUTRAL[150]}`, textAlign: "left" }}
			>
				{/* Wraps rather than clips: a department with more jobs than fit gets a second line, never a
				    hidden job. The census figures keep their own column either way. */}
				<Box sx={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.25, rowGap: 0.25 }}>
					<GapsJobShortfalls jobTotals={shownTotals} dots={dots} />
					{isNarrowed && (
						<Typography component="span" data-testid="gaps-metrics-filtered-note" sx={METRIC_LABEL_SX}>
							{t("gaps.metrics.filtered")}
						</Typography>
					)}
				</Box>
				{dayStats && dayHppd && <GapsCensusAndHppd dayStats={dayStats} dayHppd={dayHppd} />}
				<ExpandMoreIcon
					sx={{
						fontSize: 16,
						color: INK.MUTED,
						flex: "none",
						transform: isExpanded ? "rotate(180deg)" : "none",
						transition: reduceMotion ? "none" : `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					}}
				/>
			</ButtonBase>

			{/* Opens with a height reveal and closes a touch faster — the house asymmetry: the user
			    deliberates over showing something, the system should be prompt taking it back. */}
			<Collapse
				in={isExpanded}
				timeout={reduceMotion ? 0 : { enter: MOTION.DURATION.ENTRANCE_MD, exit: MOTION.DURATION.INLINE_EXIT }}
				easing={MOTION.EASE_OUT}
				unmountOnExit
			>
				<GapsMetricsDetail dayStats={dayStats} dayHppd={dayHppd} pickupBonuses={pickupBonuses} date={date} legend={legend} />
			</Collapse>
		</Box>
	);
};
